import { supabase } from './supabase';
import { syncManager } from './syncManager';
import { pendingChanges, serialQueue, withId, writeDayRow } from './dayRowCas';
import { refreshTodayOnFocus } from './dayLogFocusRefresh';
import { toLocalYMD } from '../utils/hydration';
import { withMark, type DoseMark } from '../utils/intakeSchedule';

/**
 * Today's ticks for the four "list of ids per day" logs — supplements and medication
 * (`taken_ids`), grooming and recovery (`done_ids`). Design: docs/DAY_LOG_STALE_WRITES.md.
 *
 * ⚠️⚠️ A TICK IS A CHANGE, NEVER THE DAY. Each store used to upsert today's WHOLE id
 * list from its own memory (`base = rolledOver ? [] : today…`), so a stale tab brought
 * back a tick undone elsewhere or dropped ticks made elsewhere — and a habit tick or a
 * finished routine (which tick grooming/recovery for you) did it with no one looking.
 * Now one id is set on or off in the SERVER's list (read, change, compare-and-swap).
 *
 * The screen shows the server's list with this tab's unsaved ticks on top, so a quick
 * second tap is decided against the first (a double tap ticks, then unticks). When the
 * tab comes back, today is read again (`refresh`, lib/dayLogFocusRefresh).
 */

interface IdRow {
  log_date: string;
  scheduled: number | null;
  updated_at: string;
  [column: string]: unknown;
}

export function dayIdLog(o: {
  table: string;
  column: 'taken_ids' | 'done_ids';
  /** For the console: "Error logging <what>". */
  what: string;
  /**
   * Today's scheduled count (the current active list), stored with each change — given the
   * day's ids as they will be saved (the intake logs count a dose marked before its item was paused).
   */
  scheduled: (ids: readonly string[]) => number;
  /** Put `date`'s ids on screen. */
  show: (date: string, ids: string[]) => void;
  /** The user the store has loaded (its `initializedUserId`) — a refresh needs a loaded store. */
  loadedFor: () => string | null;
}) {
  let enqueue = serialQueue();
  const pending = pendingChanges<string[]>();
  /** The server's ids for the day this tab last read or wrote. */
  let server: { date: string; ids: string[] } | null = null;
  let loading: Promise<void> | null = null;
  /**
   * ⚠️⚠️ MOVED BY `clear` (sign-out). A tick, or the store's load, notes it before its first
   * await and drops its answer when it has moved: work still in flight for the account that
   * signed out must not land in — or be written for — the next account.
   */
  let epoch = 0;

  const idsOf = (row: IdRow | null): string[] => {
    const v = row?.[o.column];
    return Array.isArray(v) ? (v as string[]) : [];
  };
  const view = (date: string) => pending.over(date, server?.date === date ? server.ids : []);

  /**
   * Re-read TODAY's ids (the calendar's today) and show them with this tab's unsaved ticks
   * on top. ⚠️ Read only, and in the write queue: a tick in flight lands first (and is read
   * back), a tick tapped meanwhile stays on screen (`pending`) until its own write answers.
   */
  const refresh = () => enqueue(async () => {
    const { user } = await syncManager.safeAuthCheck();
    if (!user || o.loadedFor() !== user.id) return;
    const date = toLocalYMD(new Date());
    const { data, error } = await supabase
      .from(o.table)
      .select(`log_date, ${o.column}, scheduled, updated_at`)
      .eq('user_id', user.id)
      .eq('log_date', date)
      .maybeSingle();
    if (error) { console.error(`Error refreshing ${o.what}:`, error); return; }
    if (o.loadedFor() !== user.id) return;                  // signed out while it read
    server = { date, ids: idsOf((data ?? null) as IdRow | null) };
    o.show(date, view(date));
    focus.read(date);
  });
  const focus = refreshTodayOnFocus(refresh);

  /**
   * One change to TODAY's list (the calendar's — not the day this tab loaded), applied to the
   * server's list. ⚠️ It is on screen BEFORE anything is awaited, so a second tap right after
   * is decided against this one. `edit` hands back the same array when nothing changes.
   */
  function change(edit: (ids: string[]) => string[]): Promise<boolean> {
    const at = epoch;
    const date = toLocalYMD(new Date());
    const op = pending.add(date, edit);
    o.show(date, view(date));
    return enqueue(async () => {
      const { user } = await syncManager.safeAuthCheck();
      // Signed out while it waited its turn: the account signed in NOW (if any) never
      // gets the last one's tick.
      if (at !== epoch) return false;
      let ok = false;
      if (user) {
        const res = await writeDayRow<IdRow>(
          { table: o.table, userId: user.id, date, columns: `log_date, ${o.column}, scheduled` },
          (row) => {
            const before = idsOf(row);
            const after = edit(before);
            return after === before ? null : { [o.column]: after, scheduled: o.scheduled(after) };
          },
        );
        if (at !== epoch) return res.ok; // signed out while it saved: not shown to the next account
        ok = res.ok;
        if (res.ok) server = { date, ids: idsOf(res.row) };
        else console.error(`Error logging ${o.what}:`, res.error);
      }
      pending.settle(op);
      o.show(date, view(date));
      return ok;
    });
  }

  return {
    /**
     * Run the store's load in the write queue. ⚠️ A call while it runs JOINS it — it used
     * to return at once, so a caller that awaited it acted on a day that wasn't loaded.
     */
    load(run: () => Promise<void>): Promise<void> {
      if (loading) return loading;
      // ⚠️ Clears only its own: a sign-out drops this load and the next account starts its own.
      const mine: Promise<void> = enqueue(run).finally(() => { if (loading === mine) loading = null; });
      loading = mine;
      return mine;
    },

    /** The sign-out generation — a load notes it and drops its answer when `clear` has moved it. */
    generation: () => epoch,

    /** The load read `date`'s ids: show them, with any tap made during the load on top. */
    loaded(date: string, ids: string[]) {
      server = { date, ids };
      o.show(date, view(date));
      focus.read(date);
    },

    refresh,

    /**
     * Tick (`on`) or untick one id for TODAY (the calendar's — not the day this tab
     * loaded). ⚠️ It is on screen BEFORE anything is awaited, so a second tap right after
     * is decided against this one.
     */
    set(id: string, on: boolean): Promise<boolean> {
      return change(ids => withId(ids, id, on));
    },

    /**
     * Mark one dose taken, skipped (`~<key>` in the same list) or neither — the same
     * change-not-copy write as `set` (utils/intakeSchedule `withMark`).
     */
    mark(key: string, mark: DoseMark): Promise<boolean> {
      return change(ids => withMark(ids, key, mark));
    },

    /**
     * ⭐ Today's count again, its ids untouched (Supplements polish round, fix 1): the list changed
     * today — a Pause, a Resume, an item added — after the day's row was written, so the row says
     * what is due NOW. Else the next day reads the old count: a dose paused at noon "not recorded".
     * Only a row that exists (the day's first tick writes its own count), only when it differs —
     * the same compare-and-swap as a tick, in the same queue (a tick in flight lands first).
     */
    rescore(): Promise<boolean> {
      const at = epoch;
      const date = toLocalYMD(new Date());
      return enqueue(async () => {
        const { user } = await syncManager.safeAuthCheck();
        // signed out (or another account) meanwhile: never the next account's row
        if (at !== epoch || !user || o.loadedFor() !== user.id) return false;
        const res = await writeDayRow<IdRow>(
          { table: o.table, userId: user.id, date, columns: `log_date, ${o.column}, scheduled` },
          (row) => {
            if (!row) return null;
            const scheduled = o.scheduled(idsOf(row));
            return row.scheduled === scheduled ? null : { scheduled };
          },
        );
        if (!res.ok) console.error(`Error recounting ${o.what}:`, res.error);
        return res.ok;
      });
    },

    /** Sign-out: forget the day, the unsaved ticks and the work in flight. */
    clear() {
      epoch += 1;
      // A fresh queue: the next account's load never waits behind the old account's work.
      enqueue = serialQueue();
      loading = null;
      server = null;
      pending.clear();
      focus.forget();
    },
  };
}
